import { FC, useState } from 'react';
import { ChevronDown, ClipboardCheck } from 'lucide-react';
import { cn } from '@new/lib';
import { ExamSummary } from '@new/features/courses/utils/examHelpers';

interface StudentCourseExamProps {
  summary: ExamSummary;
}

export const StudentCourseExam: FC<StudentCourseExamProps> = ({ summary }) => {
  const [isOpen, setIsOpen] = useState(false);

  const { attemptsCount, summaryCorrectAnswersCount, summaryTotalQuestionsCount, attempts } =
    summary;

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-(--neutral-white-NW800) bg-(--neutral-white-NW1000) p-4 shadow-sm">
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="flex w-full cursor-pointer items-center justify-between gap-3 border-0 bg-transparent p-0 text-left"
        aria-expanded={isOpen}
      >
        <div className="flex min-w-0 items-center gap-3">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-(--primary-O200)">
            <ClipboardCheck className="size-4 text-(--primary-O800)" />
          </div>

          <div className="flex min-w-0 flex-col gap-2">
            <h6 className="[font-size:var(--fs-h6)] [font-family:var(--font-montserrat)]! m-0! truncate font-semibold! text-(--neutral-black-NB800)!">
              Exam
            </h6>

            <div className="flex flex-wrap items-center gap-2">
              <p className="[font-size:var(--fs-l4)] m-0! font-medium text-(--neutral-black-NB700)">
                {attemptsCount > 0
                  ? `${summaryCorrectAnswersCount}/${summaryTotalQuestionsCount} questions answered correctly`
                  : 'No exam attempts yet'}
              </p>

              {attemptsCount > 0 && (
                <>
                  <div className="size-1 rounded-full bg-(--neutral-black-NB700)" />

                  <p className="[font-size:var(--fs-l4)] m-0! font-medium text-(--neutral-black-NB700)">
                    {attemptsCount} Attempt{attemptsCount !== 1 ? 's' : ''}
                  </p>
                </>
              )}
            </div>
          </div>
        </div>

        <ChevronDown
          className={cn(
            'size-5 shrink-0 text-(--neutral-black-NB700) transition-transform',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <div className="flex flex-col gap-3 border-t border-(--neutral-white-NW800) pt-3">
          <p className="[font-size:var(--fs-p4)] m-0! font-semibold text-(--neutral-black-NB800)">
            Attempt History
          </p>

          {attempts.length === 0 ? (
            <p className="[font-size:var(--fs-l4)] m-0! font-medium text-(--neutral-black-NB700)">
              Exam attempt details are not available yet.
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              {attempts.map((attempt) => (
                <div
                  key={attempt.id}
                  className="flex flex-col gap-3 rounded-xl border border-(--neutral-white-NW800) bg-(--neutral-white-NW1000) p-3 shadow-sm"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex min-w-0 flex-col gap-1">
                      <p className="[font-size:var(--fs-p4)] m-0! font-semibold text-(--neutral-black-NB900)">
                        {attempt.label}
                      </p>

                      <p className="[font-size:var(--fs-l5)] m-0! font-medium text-(--neutral-black-NB600)">
                        {attempt.date}
                      </p>
                    </div>

                    <span className="w-fit rounded-full border border-(--primary-O300) bg-(--primary-O100) px-2 py-0.5 text-xs font-semibold text-(--primary-O900)">
                      {attempt.score}
                    </span>
                  </div>

                  <div className="h-2 overflow-hidden rounded-full bg-(--neutral-black-NB200)">
                    <div
                      className="h-full rounded-full bg-(--primary-O800)"
                      style={{ width: `${attempt.percentage}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
